import { useMutation, useQuery } from "@tanstack/react-query";
import { supabase } from "../supabase/supabase-client";
import { Fine } from "../types/entities";
import { TablesInsert, TablesUpdate } from "../types/supabase";

export const useFines = () => {
  return useQuery({
    queryKey: ["fines"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("fines")
        .select("*")
        .eq("deleted", false)
        .order("created_at", { ascending: false });
      if (error) throw error;
      return data as Fine[];
    },
  });
};

export const useGetFine = (fineId?: string) => {
  return useQuery({
    queryKey: ["fine", fineId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("fines")
        .select("*")
        .eq("id", Number(fineId))
        .maybeSingle();
      if (error) throw error;
      return data;
    },
    enabled: !!fineId,
  });
};

export interface AddFinePayload {
  logeMemberId: number;
  amountKr: number;
  reason: string;
  issuedBy: number;
  witnessIds?: number[];
}

export const useAddFine = () => {
  return useMutation(async (payload: AddFinePayload) => {
    const insert: TablesInsert<"fines"> = {
      loge_member_id: payload.logeMemberId,
      amount_kr: payload.amountKr,
      reason: payload.reason,
      issued_by: payload.issuedBy,
    };
    const { data, error } = await supabase
      .from("fines")
      .insert(insert)
      .select()
      .single();
    if (error) throw error;

    if (payload.witnessIds?.length) {
      const witnessRows: TablesInsert<"fine_witnesses">[] =
        payload.witnessIds.map((id) => ({
          fine_id: data.id,
          loge_member_id: id,
        }));
      const { error: witnessError } = await supabase
        .from("fine_witnesses")
        .insert(witnessRows);
      if (witnessError) throw witnessError;
    }
    return data;
  });
};

export interface UpdateFinePayload {
  fineId: number;
  updates: TablesUpdate<"fines">;
}

export const useUpdateFine = () => {
  return useMutation(async ({ fineId, updates }: UpdateFinePayload) => {
    const { error } = await supabase
      .from("fines")
      .update(updates)
      .eq("id", fineId);
    if (error) throw error;
  });
};

export const useDeleteFine = () => {
  return useMutation(async (fineId: number) => {
    const { error } = await supabase
      .from("fines")
      .update({ deleted: true })
      .eq("id", fineId);
    if (error) throw error;
  });
};
